import React from 'react';
import { Box } from '@mui/material';

/**
 * Formats the stats and futureDates for the Messages Panel tabs.
 * Returns an array: [SummaryTabContent, FutureDatesTabContent, Reserved1, Reserved2]
 *
 * @param {Array<string>} stats - List of summary/stat lines from backend
 * @param {Array<string>} futureDates - List of future date lines from backend (optional)
 * @returns {Array<React.ReactNode>} tabContents
 */
export function formatMessagesPanelTabs(stats = [], futureDates = []) {
  console.log('[formatMessagesPanelTabs] called with:', { stats, futureDates });
  console.log('[formatMessagesPanelTabs] stats values:', stats);
  console.log('[formatMessagesPanelTabs] futureDates values:', futureDates);
  console.log('[formatMessagesPanelTabs] called with:', { stats, futureDates });
  // Use the arrays as provided: summaryLines = stats, futureDatesLines = futureDates
  let summaryLines = stats;
  let futureDatesLines = futureDates;

  // Extract Long Side Gains and Short Side Gains for Net Gain computation
  let longSideGains = null;
  let shortSideGains = null;
  summaryLines.forEach(line => {
    if (typeof line !== 'string') return;
    const lm = line.match(/^Long Side Gains:\s*([\d.-]+)$/);
    if (lm) longSideGains = parseFloat(lm[1]);
    const sm = line.match(/^Short Side Gains:\s*([\d.-]+)$/);
    if (sm) shortSideGains = parseFloat(sm[1]);
  });

  // Row style shared by all summary rows
  const rowSx = (bgColor) => ({
    display: 'flex',
    justifyContent: 'space-between',
    bgcolor: bgColor,
    px: 2,
    py: 1,
    fontFamily: 'monospace',
    fontSize: 16,
    borderRadius: 2,
    m: 0.5,
    border: '1px solid #e0e0e0',
  });

  // Alternating row background for summary
  const summaryRows = [];
  let rowIdx = 0;
  summaryLines.forEach((line, idx) => {
    // Bold all numbers after a colon in any stat line
    // Safety check: Ensure 'line' is a string before calling .match()
    const match = typeof line === 'string' ? line.match(/^(.*?:)\s*([\d.-]+)$/) : null;
    const bgColor = rowIdx % 2 === 0 ? '#e5ffe5' : '#ffeaea';
    if (match) {
      const label = match[1].toLowerCase();
      const isCount = label.includes('# of');
      const displayNum = isCount ? String(Math.round(parseFloat(match[2]))) : parseFloat(match[2]).toFixed(2);
      summaryRows.push(
        <Box key={idx} sx={rowSx(bgColor)}>
          <span>{match[1]}</span>
          <Box component="span" sx={{ fontWeight: 'bold', textAlign: 'right', minWidth: 50, fontFamily: 'monospace' }}>{displayNum}</Box>
        </Box>
      );
    } else {
      summaryRows.push(
        <Box key={idx} sx={rowSx(bgColor)}>{line}</Box>
      );
    }
    rowIdx++;

    // Insert Net Gain row right after Short Side Gains
    if (typeof line === 'string' && line.startsWith('Short Side Gains:') && longSideGains !== null && shortSideGains !== null) {
      const netGainNum = (longSideGains - shortSideGains).toFixed(2);
      summaryRows.push(
        <Box key={`net-gain-${idx}`} sx={{
          ...rowSx('#fff9c4'),
          border: '2px solid #f9a825',
          background: 'linear-gradient(90deg, #fff9c4 0%, #fff176 100%)',
          '@keyframes flashNum': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.3 },
          },
        }}>
          <span style={{ fontWeight: 'bold' }}>Net Gain (longside - shortside):</span>
          <Box component="span" sx={{
            fontWeight: 'bold', textAlign: 'right', minWidth: 50, fontFamily: 'monospace',
            animation: 'flashNum 1.2s ease-in-out infinite',
          }}>{netGainNum}</Box>
        </Box>
      );
      rowIdx++;
    }
  });
  const summaryTab = <Box>{summaryRows}</Box>;

  // Future Dates tab: each line formatted as yyyy-MM-dd -to- yyyy-MM-dd
  const futureTab = (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
      {futureDatesLines.map((line, idx) => {
        // Try to parse date1-to-date2
        let date1 = '', date2 = '';
        // Safety check: Ensure 'line' is a string before calling .match()
        const match = typeof line === 'string' ? line.match(/^(\d{4}-\d{2}-\d{2})[- ]*to[- ]*(\d{4}-\d{2}-\d{2})$/) : null;
        if (match) {
          date1 = match[1];
          date2 = match[2];
        } else {
          // fallback: show raw line
          return (
            <Box key={idx} sx={{ gridColumn: 'span 3', bgcolor: idx % 2 === 0 ? '#e5ffe5' : '#ffeaea', px: 2, py: 1, fontFamily: 'monospace', fontSize: 16, borderRadius: 2, m: 0.5, border: '1px solid #e0e0e0' }}>{line}</Box>
          );
        }
        const bgColor = idx % 2 === 0 ? '#e5ffe5' : '#ffeaea';
        return [
          <Box key={idx + '-date1'} sx={{ bgcolor: bgColor, px: 2, py: 1, fontFamily: 'monospace', fontSize: 16, borderRadius: 2, m: 0.5, border: '1px solid #e0e0e0', textAlign: 'right' }}>{date1}</Box>,
          <Box key={idx + '-sep'} sx={{ bgcolor: bgColor, px: 2, py: 1, fontFamily: 'monospace', fontSize: 16, borderRadius: 2, m: 0.5, border: '1px solid #e0e0e0', textAlign: 'center', color: '#888' }}>to</Box>,
          <Box key={idx + '-date2'} sx={{ bgcolor: bgColor, px: 2, py: 1, fontFamily: 'monospace', fontSize: 16, borderRadius: 2, m: 0.5, border: '1px solid #e0e0e0', textAlign: 'left' }}>{date2}</Box>
        ];
      })}
    </Box>
  );

  // Reserved tabs (empty for now)
  const reserved1 = <Box sx={{ px: 2, py: 1, color: '#aaa' }}>Reserved</Box>;
  const reserved2 = <Box sx={{ px: 2, py: 1, color: '#aaa' }}>Reserved</Box>;

  console.log('[formatMessagesPanelTabs] returns:', { summaryTab, futureTab, reserved1, reserved2 });
  return [summaryTab, futureTab, reserved1, reserved2];
}
